import { secureFetch, ROUTES } from "@utils/apiClient";
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ShieldCheck, AlertCircle, Calendar, ArrowRight, FileCheck, MailCheck } from 'lucide-react';

/**
 * SCHEDULING GATEKEEPER
 * Professional diagnostic booking restricted to Phase 01 verified clients.
 * Frames the meeting as a 'Forensic Review' rather than a service call.
 */
const SchedulingGatekeeper = ({ backendUrl }) => {
    const { briefId } = useParams();
    const [verificationStatus, setStatus] = useState('verifying'); // verifying, authorized, denied, submitted
    const [projectData, setProjectData] = useState(null);
    const [recoveryEmail, setRecoveryEmail] = useState('');

    useEffect(() => {
        const verifyAccess = async () => {
            try {
                // Connecting to the Micheals Infrastructure Advisory Administrative API
                // Add the backtick at the beginning of the string
                const response = await secureFetch(ROUTES.VERIFY_BRIEF(briefId));
                const data = await response.json();

                if (data.valid) {
                    setProjectData(data.project);
                    setStatus('authorized');
                } else {
                    setStatus('denied');
                }
            } catch (err) {
                console.error("Verification protocol failure:", err);
                setStatus('denied');
            }
        };

        if (briefId) verifyAccess();
        else setStatus('denied');
    }, [briefId, backendUrl]);

    const handleRecoverySubmit = async (e) => {
        e.preventDefault();
        try {
            await secureFetch(`${backendUrl}/api/admin/report-access-breach`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: recoveryEmail, attemptId: briefId })
            });
            setStatus('submitted');
        } catch (err) {
            console.error("Recovery log failed");
        }
    };

    // --- RENDER: VERIFYING ---
    if (verificationStatus === 'verifying') {
        return (
            <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-6">
                <motion.div 
                    animate={{ rotate: 360 }} 
                    transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    className="w-8 h-8 border-t-2 border-amber-600 rounded-full mb-4"
                />
                <p className="text-[10px] uppercase tracking-[0.4em] text-stone-400 font-bold">Verifying Strategic Credentials</p>
            </div>
        );
    }

    // --- RENDER: DENIED / RECOVERY ---
    if (verificationStatus === 'denied' || verificationStatus === 'submitted') {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6 text-center">
                <div className="max-w-md w-full">
                    <AnimatePresence mode="wait">
                        {verificationStatus === 'denied' ? (
                            <motion.div 
                                key="denied-form"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <AlertCircle className="mx-auto text-amber-900/20 mb-6" size={48} />
                                <h2 className="font-serif text-2xl text-stone-900 mb-4">Access Restricted</h2>
                                <p className="text-stone-500 text-sm font-sans leading-relaxed mb-8">
                                    Phase 01: Forensic Auditing has not been initialized for this session. 
                                    Provide your professional email to request manual verification from the Principal.
                                </p>
                                
                                <form onSubmit={handleRecoverySubmit} className="space-y-4 mb-8">
                                    <input 
                                        type="email"
                                        required
                                        value={recoveryEmail}
                                        onChange={(e) => setRecoveryEmail(e.target.value)}
                                        placeholder="professional@firm.com"
                                        className="w-full bg-white border border-stone-200 p-4 text-xs font-mono focus:outline-none focus:border-amber-600 transition-colors shadow-sm"
                                    />
                                    <button type="submit" className="w-full py-4 bg-stone-900 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-amber-700 transition-all flex items-center justify-center gap-2">
                                        Request Manual Audit <ArrowRight size={14} />
                                    </button>
                                </form>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="success-msg"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                            >
                                <MailCheck className="mx-auto text-green-600 mb-6" size={48} />
                                <h2 className="font-serif text-2xl text-stone-900 mb-4">Verification Dispatched</h2>
                                <p className="text-stone-500 text-sm mb-8">
                                    Your request has been logged. The Principal will review your session credentials shortly.
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    
                    <Link to="/services" className="text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-stone-900 transition-colors border-t border-stone-200 pt-6 block">
                        Initialize New Consultation Path
                    </Link>
                </div>
            </div>
        );
    }

    // --- RENDER: AUTHORIZED ---
    return (
        <div className="min-h-screen bg-stone-50 p-6 md:p-12 lg:p-24 flex flex-col items-center">
            <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl w-full"
            >
                {/* Status Bar */}
                <div className="flex justify-between items-center mb-16 border-b border-stone-200 pb-8">
                    <div>
                        <h1 className="text-[10px] uppercase tracking-[0.5em] text-stone-400 font-bold">Diagnostic Scheduling</h1>
                        <p className="text-3xl font-serif text-stone-900 mt-2">Secure Consultation Portal</p>
                    </div>
                    <div className="hidden md:flex items-center gap-4 text-green-700 bg-green-50 px-4 py-2 rounded-full border border-green-100">
                        <ShieldCheck size={16} />
                        <span className="text-[10px] uppercase font-bold tracking-widest">Credentials Verified</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Project Overview */}
                    <div className="lg:col-span-4 space-y-8">
                        <div className="bg-white border border-stone-200 p-8 rounded-sm shadow-sm">
                            <h3 className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-6">Verified Project</h3>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-[9px] uppercase text-amber-700 font-bold">Principal Contact</p>
                                    <p className="font-serif text-lg text-stone-900">{projectData.customerName}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] uppercase text-amber-700 font-bold">Brief Identifier</p>
                                    <p className="font-mono text-[10px] text-stone-500">{briefId.substring(0, 12)}...</p>
                                </div>
                                <div className="pt-4 flex items-center gap-2 text-stone-400">
                                    <FileCheck size={14} className="text-green-600" />
                                    <span className="text-[10px] italic">Strategic Brief Dispatched</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-stone-900 text-stone-400 rounded-sm">
                            <p className="text-[10px] italic leading-relaxed font-serif">
                                "The Diagnostic Consultation is a technical deep-dive into the assets archived during Phase 01. This session will finalize the forensic blueprint."
                            </p>
                        </div>
                    </div>

                    {/* The Calendar Embed */}
                    <div className="lg:col-span-8">
                        <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden min-h-[600px] flex flex-col">
                            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Calendar size={18} className="text-amber-600" />
                                    <span className="text-sm font-serif text-stone-800">Available Consultation Windows</span>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-stone-400 uppercase font-bold">
                                    <Clock size={12} /> GMT -05:00
                                </div>
                            </div>
                            
                            <div className="flex-grow flex items-center justify-center bg-stone-50/50 p-12">
                                <div className="text-center w-full max-w-sm">
                                    <p className="text-stone-400 text-xs italic mb-6">
                                        Principal's calendar is live. Select a forensic review window below.
                                    </p>
                                    
                                    <button 
                                        onClick={() => window.open(`https://calendly.com/michaels-design/diagnostic?name=${encodeURIComponent(projectData.customerName)}&email=${encodeURIComponent(projectData.email)}`, '_blank')}
                                        className="w-full py-4 bg-stone-900 text-white font-bold uppercase text-[10px] tracking-widest hover:bg-amber-600 transition-all flex items-center justify-center gap-3 shadow-lg"
                                    >
                                        Open Scheduling Interface <ArrowRight size={14} />
                                    </button>

                                    <div className="mt-8 pt-8 border-t border-stone-200/60">
                                        <p className="text-[9px] text-stone-400 uppercase tracking-tighter font-bold">Secure Access Node: {briefId.substring(0,8)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default SchedulingGatekeeper;